import * as React from 'react';
import { IConfig } from '@cian/config/shared';
import { ConsultantWidgetDesktop, prepareUserData } from '@cian/newbuilding-chat-component';
import { useSelector, useDispatch } from 'react-redux';

import { chatToggleOpen } from '../../actions/nebuildingChat';
import { CHAT_OPEN_DELAY, CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL } from '../../constants/newbuildingChat';
import { isNewbuildingChatOpenedSelector } from '../../selectors/newbuildingChat';
import { getUser } from '../../selectors/user';
import { isNeedToShow, updateShownTimestamp } from '../../utils/newbuildingChat/periodicActions';
import { trackShowButton, trackClickChatOpen, trackClickChatClose } from './analytics';
import { prepareMessangers } from './utils/prepareMessangers';

interface INewbuildingChatContainerProps {
  config: IConfig;
}

export function NewbuildingChatContainer({ config }: INewbuildingChatContainerProps) {
  const dispatch = useDispatch();
  const [needForceOpen, setNeedForceOpen] = React.useState(false);

  const toggleOpen = React.useCallback(
    (isOpen: boolean) => {
      dispatch(chatToggleOpen(isOpen));

      if (isOpen) {
        setNeedForceOpen(false);
        trackClickChatOpen();
      } else {
        trackClickChatClose();
      }
    },
    [needForceOpen],
  );

  const isOpen = useSelector(isNewbuildingChatOpenedSelector);
  const user = useSelector(getUser);

  const accountName = config.get<string>('webim.accountName');
  const needForceOpenRS = config.get<boolean>('newbuildingChat.needForceOpen');

  const webimProps = React.useMemo(
    () => ({
      accountName: accountName as string,
      userData: prepareUserData(user),
    }),
    [],
  );
  const messangersLinks = React.useMemo(() => prepareMessangers(config), []);

  const onReady = React.useCallback(() => {
    if (!!accountName && needForceOpen) {
      setTimeout(() => {
        dispatch(chatToggleOpen(true));
        setNeedForceOpen(false);
        updateShownTimestamp(CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL);
      }, CHAT_OPEN_DELAY);
    }
  }, [accountName, needForceOpen]);

  React.useEffect(() => {
    if (needForceOpenRS && isNeedToShow(CHAT_FORCE_OPEN_KEY)) {
      setNeedForceOpen(true);
    }
  }, []);

  React.useEffect(() => {
    if (accountName) {
      trackShowButton();
    }
  }, [accountName]);

  if (!accountName) {
    return null;
  }

  return (
    <ConsultantWidgetDesktop
      webimProps={webimProps}
      chatOpened={isOpen}
      messangersLinks={messangersLinks}
      toggleOpen={toggleOpen}
      onReady={onReady}
    />
  );
}
